import {
  Box, Card, Chip, IconButton, InputBase, Pagination, Stack, Tooltip, Typography,
} from "@mui/material";
import { ArrowOutwardRounded, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { motion, useReducedMotion } from "framer-motion";
import { useProductLimit } from "../../context/ProductFilterContext";

const MotionBox = motion.create(Box);
const ease = [0.22, 1, 0.36, 1];

export default function ProductSection({ products, user, onSelectProduct, onSetPage, page, pages }) {
  const limit = useProductLimit();
  const reduceMotion = useReducedMotion();
  const getPrice = (product) => user?.taxId ? product.priceWholesale : product.priceUnitary;

  const paginationInput = (
    <InputBase
      type="number" value={page}
      onChange={(event) => {
        let value = Number(event.target.value);
        if (Number.isNaN(value)) return;
        onSetPage(Math.max(1, Math.min(pages, value)));
      }}
      inputProps={{ min: 1, max: pages, "aria-label": "Go to page", style: { textAlign: "center", width: 44, fontWeight: 700 } }}
      sx={{ px: 1, py: .55, borderRadius: 99, border: "1px solid", borderColor: "divider", bgcolor: "background.paper", fontSize: 14 }}
    />
  );

  if (!products.length) {
    return <Box sx={{ py: 14, textAlign: "center" }}><Typography variant="h3">Nothing here—yet.</Typography><Typography color="text.secondary" sx={{ mt: 1.5 }}>Try a different name, SKU or category.</Typography></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(3,minmax(0,1fr))" }, gap: { xs: 2, md: 3 }, gridAutoFlow: "dense" }}>
        {products.map((product, index) => {
          const featured = index === 0 && products.length > 2;
          return (
            <MotionBox
              key={product.sku}
              initial={reduceMotion ? false : { opacity: 0, y: 34, scale: .985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: .58, delay: reduceMotion ? 0 : Math.min(index * .055, .38), ease }}
              sx={{ gridColumn: { sm: featured ? "span 2" : "auto" }, gridRow: { lg: featured ? "span 2" : "auto" } }}
            >
              <Card
                component="article"
                onClick={() => onSelectProduct(product)}
                tabIndex={0}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onSelectProduct(product); }}
                sx={{
                  height: "100%", minHeight: featured ? { xs: 560, lg: 810 } : 430,
                  position: "relative", overflow: "hidden", cursor: "pointer", borderRadius: { xs: 3, md: 4 },
                  bgcolor: featured ? "#15261C" : index % 3 === 1 ? "#E9DDCD" : "#F3EDE3",
                  color: featured ? "white" : "text.primary",
                  isolation: "isolate", border: "1px solid rgba(24,36,29,.08)",
                  boxShadow: featured ? "0 30px 90px rgba(24,36,29,.2)" : "0 16px 45px rgba(35,45,37,.08)",
                  transition: "box-shadow 300ms ease, transform 300ms ease",
                  "&::before": { content: '""', position: "absolute", inset: "auto -20% -45%", height: "75%", borderRadius: "50%", background: featured ? "rgba(184,103,62,.26)" : "rgba(255,255,255,.72)", filter: "blur(55px)", transition: "transform 600ms ease", zIndex: -1 },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    zIndex: -1,
                    pointerEvents: "none",
                    background: featured
                      ? "radial-gradient(circle at 22% 18%, rgba(255,255,255,.09), transparent 32%), linear-gradient(145deg, rgba(255,255,255,.05), transparent 42%)"
                      : "linear-gradient(145deg, rgba(255,255,255,.72), rgba(255,255,255,0) 44%), radial-gradient(circle at 72% 18%, rgba(255,255,255,.75), transparent 35%)",
                  },
                  "&:hover": { transform: "translateY(-7px)", boxShadow: "0 34px 90px rgba(35,45,37,.2)" },
                  "&:hover::before": { transform: "translateY(-12%) scale(1.12)" },
                  "&:hover .catalog-photo-stage": { transform: featured ? "translateY(-12px) scale(1.018) rotate(.45deg)" : "translateY(-9px) scale(1.035) rotate(-.6deg)" },
                  "&:hover .catalog-product-image": { transform: "scale(1.045)" },
                  "&:hover .catalog-product-action": { transform: "translate(0,0)", opacity: 1 },
                }}
              >
                <Box sx={{ position: "absolute", inset: 0, display: "grid", gridTemplateRows: featured ? { xs: "1fr auto", md: "1fr auto" } : "1fr auto" }}>
                  <Box sx={{ position: "relative", minHeight: featured ? { xs: 370, lg: 585 } : 292, p: featured ? { xs: 3, md: 5 } : 2.4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box sx={{ position: "absolute", top: 20, left: 20, right: 20, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2 }}>
                      <Chip label={featured ? "Featured piece" : `No. ${String(index + 1).padStart(2, "0")}`} size="small" sx={{ color: featured ? "white" : "#17241c", bgcolor: featured ? "rgba(255,255,255,.12)" : "rgba(255,253,248,.86)", border: "1px solid", borderColor: featured ? "rgba(255,255,255,.22)" : "rgba(24,36,29,.12)", backdropFilter: "blur(12px)", boxShadow: "0 8px 22px rgba(24,36,29,.08)" }} />
                      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".14em", color: featured ? "rgba(255,255,255,.72)" : "rgba(24,36,29,.55)" }}>SKU {product.sku}</Typography>
                    </Box>
                    <Box
                      className="catalog-photo-stage"
                      sx={{
                        width: "100%",
                        height: "100%",
                        maxHeight: featured ? 530 : 285,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "transform 650ms cubic-bezier(.16,1,.3,1)",
                        transform: featured ? "rotate(-.8deg)" : "rotate(.3deg)",
                        willChange: "transform",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: featured ? { xs: 300, lg: 500 } : 235,
                          p: featured ? { xs: 1.5, sm: 2.2, md: 3 } : 1.35,
                          borderRadius: featured ? { xs: 2.5, md: 3.25 } : 2.25,
                          background: "linear-gradient(145deg, #ffffff, #fffdf8)",
                          border: "1px solid rgba(24,36,29,.08)",
                          boxShadow: featured
                            ? "0 34px 70px rgba(0,0,0,.34), inset 0 0 0 8px rgba(245,239,227,.72)"
                            : "0 24px 48px rgba(86,68,45,.14), inset 0 0 0 6px rgba(255,255,255,.74)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          component="img"
                          className="catalog-product-image"
                          src={product.image}
                          alt={product.name}
                          loading="lazy"
                          sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        background: "#fff",
                        display: "block",
                        filter: "none",
                        transition: "transform 650ms cubic-bezier(.16,1,.3,1)",
                        willChange: "transform",
                      }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ position: "relative", p: featured ? { xs: 3, md: 4.5 } : 2.5, pt: featured ? 2 : 1.5, display: "grid", gridTemplateColumns: "1fr auto", gap: 2, alignItems: "end", background: featured ? "linear-gradient(0deg,rgba(10,20,14,.52),transparent)" : "transparent" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ textTransform: "uppercase", letterSpacing: ".13em", fontSize: 10.5, fontWeight: 700, opacity: .56, mb: .7 }}>{user?.taxId ? "Wholesale collection" : "Rustic World collection"}</Typography>
                      <Tooltip title={product.name}><Typography variant={featured ? "h3" : "h5"} sx={{ fontSize: featured ? { xs: "2rem", md: "3rem" } : "1.25rem", lineHeight: 1.05, display: "-webkit-box", WebkitLineClamp: featured ? 3 : 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}>{product.name}</Typography></Tooltip>
                      <Typography sx={{ mt: 1.2, fontFamily: '"DM Serif Display",serif', fontSize: featured ? 28 : 22, color: featured ? "#DCCBB6" : "secondary.dark" }}>${getPrice(product).toFixed(2)}</Typography>
                    </Box>
                    <IconButton className="catalog-product-action" aria-label={`View ${product.name}`} sx={{ color: featured ? "#18241D" : "white", bgcolor: featured ? "#DCCBB6" : "primary.main", opacity: { xs: 1, md: featured ? 1 : 0 }, transform: { xs: "none", md: featured ? "none" : "translate(-8px,8px)" }, transition: "transform 260ms ease, opacity 260ms ease, background-color 180ms ease", "&:hover": { bgcolor: "secondary.main", color: "white" } }}><ArrowOutwardRounded /></IconButton>
                  </Box>
                </Box>
              </Card>
            </MotionBox>
          );
        })}
      </Box>

      {pages > 0 && <Box sx={{ display: "flex", justifyContent: "center", mt: 7 }}>
        {limit === 1 ? <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1, borderRadius: 99, bgcolor: "rgba(255,253,248,.8)", border: "1px solid", borderColor: "divider", backdropFilter: "blur(12px)" }}><IconButton onClick={() => onSetPage(page - 1)} disabled={page <= 1}><ChevronLeft /></IconButton><Typography fontWeight={700}>{page} / {pages}</Typography><IconButton onClick={() => onSetPage(page + 1)} disabled={page >= pages}><ChevronRight /></IconButton>{paginationInput}</Stack> : <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={2}><Pagination count={pages} page={page} onChange={(_, value) => onSetPage(value)} color="primary" shape="rounded" />{paginationInput}</Stack>}
      </Box>}
    </Box>
  );
}
